import type { TypographyVariantProps } from "../../atoms/typography";
import { cva } from "../../lib";
import type { CardState } from "./types";

type TypographyColor = NonNullable<TypographyVariantProps["color"]>;

/**
 * Root container variants. State drives border colour, background, and
 * (via context) the colours of `Card.Title` / `Card.Description`.
 *
 * Padding lives on the root (`layout`, `p-spacing-md` by default) per DS-035
 * so sub-components stay layout-only. Consumers using `Card.Image` as a full-bleed hero can
 * escape the padding via `className="-m-spacing-md mb-0"`. On iOS a card with
 * a `shadow` doesn't clip, so such a hero isn't cut to its corners there.
 */
export const cardRootVariants = cva("flex overflow-hidden border-solid", {
  variants: {
    // DQ2 (designer, 2026-11-02): compact keeps a pressable card 44 tall
    // (`min-h-touch-target`, in card.tsx); flush is a section on phones, with
    // no padding, border, corners, fill or shadow, and the card from 768
    layout: {
      default: "p-spacing-md",
      compact: "p-spacing-sm",
      flush:
        "p-spacing-md max-md:p-spacing-0 max-md:rounded-radius-none max-md:overflow-visible max-md:border-0 max-md:bg-transparent max-md:shadow-none",
    },
    direction: {
      col: "flex-col",
      row: "flex-row items-center",
    },
    state: {
      idle: "bg-luce-neutral-background-default border-luce-neutral-border-mediumEmphasis border-1",
      selected: "bg-luce-neutral-background-default border-luce-brand-primaryBorder border-1-5",
      disabled:
        "bg-luce-neutral-background-intermediate border-luce-neutral-border-highEmphasis border-1",
    },
    radius: {
      none: "rounded-radius-none",
      sm: "rounded-radius-sm",
      md: "rounded-radius-md",
      lg: "rounded-radius-lg",
      xl: "rounded-radius-xl",
      "2xl": "rounded-radius-2xl",
    },
    // iOS doesn't draw a layer shadow on a view that clips its content, so a
    // shadowed card doesn't clip there. Web and Android clip and draw both.
    shadow: {
      none: "shadow-none",
      sm: "shadow-card ios:overflow-visible",
      md: "shadow-bigcard ios:overflow-visible",
      lg: "shadow-modal ios:overflow-visible",
    },
    gap: {
      none: "gap-spacing-0",
      xs: "gap-spacing-xs",
      sm: "gap-spacing-sm",
      md: "gap-spacing-md",
      lg: "gap-spacing-lg",
      xl: "gap-spacing-xl",
    },
  },
  defaultVariants: {
    layout: "default",
    direction: "col",
    state: "idle",
    radius: "lg",
    shadow: "none",
    gap: "md",
  },
});

/**
 * A pressable, enabled card's feedback (DQ3): the fill of Figma's List Item
 * `.State Background` helper on hover (pointers that hover, on web) and while
 * pressed, and the shared focus ring Button and Chip draw (`web:`, since
 * css-interop drops `:focus-visible` on native)
 */
export const cardPressableClassName = [
  "web:[@media(hover:hover)]:hover:bg-luce-neutral-background-intermediate",
  "active:bg-luce-neutral-background-intermediate",
  "web:outline-none web:focus-visible:ring-focus web:focus-visible:ring-luce-brand-primaryBorder web:focus-visible:ring-offset-focus",
].join(" ");

/** Typography `color` token for `Card.Title` per state. */
export const cardTitleColor: Record<CardState, TypographyColor> = {
  idle: "neutral-foreground-default",
  selected: "neutral-foreground-default",
  disabled: "neutral-foreground-attenuated",
};

/** Typography `color` token for `Card.Description` per state. */
export const cardDescriptionColor: Record<CardState, TypographyColor> = {
  idle: "neutral-foreground-intermediate",
  selected: "neutral-foreground-intermediate",
  disabled: "neutral-foreground-attenuated",
};
